'use es6'; // Linter doesn't like IIFEs or Revealing Module Pattern

/* eslint-disable eqeqeq */

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import text from 'I18n/utils/unescapedText';
import { once, pick } from 'underscore';
import { getPath } from 'DesignData/lib/objectSelectors';
import { EMPTY_ARRAY, isArrayValuesEqual } from 'DesignData/lib/array';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { forEachNormalizedWidgets } from 'DesignData/lib/normalization/layouts/iterators';
import * as SmartWidgetTypes from 'DesignData/enums/SmartWidgetTypes';
import { getTargetType } from 'DesignData/helpers/smartObjectHelper';
import { SMART_CONTENT } from 'DesignData/constants/Scopes';
import * as PermanentlyLockedModuleTypes from 'DesignData/enums/PermanentlyLockedModuleTypes';
import { VOID, SECTIONLESS, SECTION_DELIMITER_1, SECTION_DELIMITER_2, HEADER, BODY, FOOTER } from 'DesignData/enums/LayoutSections';
import { getWidgetAncestors, getWidgetAncestry as generateWidgetAncestry } from 'DesignData/lib/normalization/layouts';
import { someNormalizedWidgets } from 'DesignData/lib/normalization/layouts/iterators';
import { findCustomWidget } from 'DesignData/lib/layouts/findCustomWidget';
import { isScoped as getIsScoped } from 'DesignData/selectors/auth';
import { isCellType, isGlobalType, isFlexColumnType, isVirtualCellType, isSectionDelimiterType, getWidgetLabel } from 'DesignData/helpers/widgets';
import { containsHtmlField } from 'DesignData/helpers/customWidgetFieldsHelper';
import { getI18nWidgetTypeLabel } from 'DesignData/helpers/widgets';
import { makeGetMeta, getBodyEditable } from 'DesignData/selectors/layouts';
import { // Selector that gets all structural widgets
getStructuralWidgetsByType // Creates a selector that gets preset widgets for layout determined by props (layoutId/revisionId).
, makeGetStructuralWidgets, makeGetGlobalWidgetSummaries, getWidgetCatalogIsInitialized } from 'DesignData/selectors/layoutWidgets';
import { getCustomWidgetsData, getCustomWidgetsByType, getSmartContentEligibleCustomWidgetsById } from 'DesignData/selectors/customWidgets';
import { getGlobalWidgetSummariesByName } from 'DesignData/selectors/globalWidgets';
import { getGlobalGroupsDataByPath } from 'DesignData/selectors/globalGroups';
import { getShowLayoutSectionEditorOptions } from 'DesignData/selectors/gates';
/*
 *
 * Basic Selectors
 *
 */

/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const getLayoutData = (state, props) => {
  const {
    layoutId,
    assetId,
    revisionId
  } = props;
  const id = layoutId || assetId;
  return revisionId ? getPath(state, ['layoutRevisions', 'layouts', id, 'revisions', revisionId, 'layoutData']) : getPath(state, ['unifiedLayouts', 'byId', id, 'present', 'layoutData']);
};
/**
 * Required Props:
 *  - widgetId
 */

const getWidgetIdFromProps = (__, props) => props.widgetId;
/**
 * Required Props:
 *  - widgetIds[]
 */


const getWidgetIdsFromProps = (__, props) => props.widgetIds;
/**
 * Required Props:
 *  layoutId
 *  widgetId
 * Optional Props:
 *  revisionId
 */


export const getWidget = (state, props) => {
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  return layoutData.widgets[props.widgetId];
};
/**
 * Required Props:
 *  layoutId
 *  widgetId
 * Optional Props:
 *  revisionId
 */

export const getRow = (state, props) => {
  const layoutData = getLayoutData(state, props);

  if (!layoutData) {
    return null;
  }

  return layoutData.rows[props.rowId];
};
export const getIsInFlexColumn = (__, props) => props.isInFlexColumn;
export const getIsInVirtualCell = (__, props) => props.isInVirtualCell;
export const getWidgetType = (state, props) => {
  const widget = getWidget(state, props);
  return widget ? widget.type : null;
};
/**
 * Required Props:
 * - layoutId
 * - widgetId
 * Optional Props:
 * - revisionId
 */

export const getIsContentOverridden = (state, props) => {
  const widget = getWidget(state, props);
  return widget && widget.isContentOverridden || false;
};
/**
 * Required Props:
 *  layoutId || assetId
 */

export const [getStagedWidgetsForDeleteConfirmation, getStagedWidgetsForGroupTypeTransformConfirmation] = ['stagedWidgetsForDeleteConfirmation', 'stagedWidgetsForGroupTypeTransformConfirmation'].map(statePathSlug => {
  return (state, props) => {
    const {
      layoutId,
      assetId
    } = props;
    return SeamlessImmutable.getIn(state, ['unifiedLayouts', statePathSlug, layoutId || assetId], EMPTY_OBJECT);
  };
});
/**
 * Required Props:
 *  none - state only selector
 *
 * @return {Function} layoutWidget => catalogWidget
 */

const getCatalogWidgetGetterForLayoutWidget = createSelector([getStructuralWidgetsByType, getGlobalGroupsDataByPath, getGlobalWidgetSummariesByName, getCustomWidgetsData, getCustomWidgetsByType], (structuralWidgetsByType, globalGroupsByPath, globalWidgetSummariesByName, customWidgetsData, customWidgetsByType) => {
  return layoutWidget => {
    switch (layoutWidget.type) {
      case 'custom_widget':
        {
          return findCustomWidget(layoutWidget, customWidgetsData);
        }

      case 'global_group':
        {
          const {
            generated_template_path: generatedTemplatePath,
            path
          } = layoutWidget.params;
          return globalGroupsByPath[generatedTemplatePath || path];
        }

      case 'global_widget':
        {
          const {
            name
          } = layoutWidget;
          return globalWidgetSummariesByName[name.toLowerCase()];
        }

      default:
        {
          if (isCellType(layoutWidget.type)) {
            return structuralWidgetsByType[layoutWidget.type];
          }

          return customWidgetsByType[layoutWidget.type];
        }
    }
  };
});
/**
 * Required Props:
 *  layoutId
 *  widgetId
 * Optional Props:
 *  revisionId
 */

export const makeGetCatalogWidgetForLayoutWidget = () => {
  return createSelector([getWidget, getCatalogWidgetGetterForLayoutWidget], (widget, getCatalogWidget) => getCatalogWidget(widget));
};
export const getCatalogWidgetForLayoutWidget = makeGetCatalogWidgetForLayoutWidget();
/*
 *
 * Helper methods
 *
 */

const isWidgetEmpty = widget => widget.rows.length === 0;
/*
 *
 * Reselelect Selector Factories
 *
 */


export const makeGetWidgetAncestors = () => {
  let ancestors = [];
  return createSelector([getLayoutData, getWidget], (layoutData, widget) => {
    if (!widget) {
      return ancestors;
    }

    const {
      id: widgetId
    } = widget;
    const {
      widgets,
      rows,
      rootWidgetId
    } = layoutData;

    if (widgetId === rootWidgetId) {
      return ancestors;
    }

    ancestors = getWidgetAncestors(widgets, rows, rootWidgetId, widgetId);
    return ancestors;
  });
};
/**
 * Required props:
 *  - layoutId | assetId
 *  - widgetId | widgetIds[]
 * Optional Props:
 *  - revisionId
 *
 * Gets all widgetIds, including children, for given widgetId or widgetIds[]
 */

export const makeGetAllWidgetIds = () => {
  let result = EMPTY_ARRAY;
  return createSelector([getLayoutData, getWidgetIdFromProps, getWidgetIdsFromProps], (layoutData, widgetId, widgetIds) => {
    if (!layoutData) {
      return result = EMPTY_ARRAY;
    }

    const ids = (Array.isArray(widgetIds) ? widgetIds : []).concat(widgetId || []);

    if (!ids.length) {
      return result = EMPTY_ARRAY;
    }

    const {
      widgets,
      rows
    } = layoutData;
    const newIds = {};
    const newList = [];
    ids.forEach(id => {
      forEachNormalizedWidgets(widgets, rows, id, widget => {
        if (newIds[widget.id]) return;
        newIds[widget.id] = true;
        newList.push(widget.id);
      });
    });
    return isArrayValuesEqual(result, newList) ? result : result = SeamlessImmutable(newList);
  });
};
export const makeGetIsWidgetGlobal = () => {
  const getCatalogWidget = makeGetCatalogWidgetForLayoutWidget();
  return createSelector([getWidget, getCatalogWidget], (widget, catalogWidget) => {
    if (isGlobalType(widget.type)) {
      return true;
    }

    if (catalogWidget && catalogWidget.global && catalogWidget.type === 'custom_widget') {
      return true;
    }

    return false;
  });
};
const PermanentlyLockedModuleTypesLegacyOnly = pick(PermanentlyLockedModuleTypes, PermanentlyLockedModuleTypes.email_can_spam, PermanentlyLockedModuleTypes.email_view_as_web_page, PermanentlyLockedModuleTypes.post_listing);
const makeGetPermanentlyLockedModuleIds = once(() => {
  let moduleIds = {};
  return createSelector([getCustomWidgetsByType], customWidgetsByType => {
    moduleIds = Object.keys(PermanentlyLockedModuleTypes).reduce((_moduleIds, _type) => {
      const _customWidget = customWidgetsByType[_type];

      if (_customWidget && !PermanentlyLockedModuleTypesLegacyOnly[_type]) {
        _moduleIds[_customWidget.moduleId] = _customWidget;
      }

      return _moduleIds;
    }, moduleIds);
    return moduleIds;
  });
});
const getGlobalWidgetSummaries = makeGetGlobalWidgetSummaries();

const makeGetHasSmartContent = () => {
  return createSelector([getWidget, getGlobalWidgetSummaries], (widget, globalWidgets) => {
    const globalWidget = widget.type === 'global_widget' && globalWidgets.widgetsByName[widget.name];
    const smartProperties = globalWidget && globalWidget.getSmartProperties ? globalWidget.getSmartProperties() : widget.getSmartProperties();
    const smartObjects = smartProperties.smart_objects;
    return !!(smartProperties.smart_type && smartObjects && smartObjects.length > 0);
  });
};

export const getHasSmartContent = makeGetHasSmartContent();
export const makeGetIsWidgetPermanentlyLocked = () => {
  const getIsWidgetGlobal = makeGetIsWidgetGlobal();
  const getPermanentlyLockedModuleIds = makeGetPermanentlyLockedModuleIds();
  const getMeta = makeGetMeta();
  return createSelector([getWidget, getMeta, getIsWidgetGlobal, getCatalogWidgetGetterForLayoutWidget, getPermanentlyLockedModuleIds, getHasSmartContent], (widget, meta, isGlobal, getCatalogWidget, permanentlyLockedModuleIds, hasSmartContent) => {
    if (meta.isGlobalGroup) {
      return true;
    }

    if (PermanentlyLockedModuleTypes[widget.type]) {
      return true;
    }

    const catalogWidget = getCatalogWidget(widget);

    if (catalogWidget && permanentlyLockedModuleIds[catalogWidget.moduleId]) {
      return true;
    }

    if (isGlobal) {
      return true;
    }

    if (widget.params.overrideable === false && hasSmartContent) {
      return true;
    }

    return false;
  });
};
export const makeGetIsWidgetLocked = () => {
  const getIsWidgetPermanentlyLocked = makeGetIsWidgetPermanentlyLocked();
  return createSelector([getWidget, getIsWidgetPermanentlyLocked], (widget, isPermanentlyLocked) => {
    if (isCellType(widget.type)) {
      return false;
    }

    if (isPermanentlyLocked) {
      return true;
    }

    return widget.params.overrideable === false;
  });
};
/**
 * Smart content
 */

export const makeGetSmartWidget = () => {
  return createSelector([getWidget, getGlobalWidgetSummaries], (widget, globalWidgets) => {
    return widget.type === 'global_widget' ? globalWidgets.widgetsByName[widget.name] : widget;
  });
};
export const makeGetWidgetSmartContentProps = () => {
  const getMeta = makeGetMeta();
  const defaultState = SeamlessImmutable({
    isEligibleForSmartContent: false,
    hasSmartContent: false,
    anonymousTargeting: false,
    targetType: null
  });
  let state = defaultState;
  return createSelector([getMeta, getWidget, getGlobalWidgetSummaries, getSmartContentEligibleCustomWidgetsById, getIsScoped, getHasSmartContent, getCustomWidgetsData, getCustomWidgetsByType], (meta, widget, globalWidgets, smartContentEligibleCustomWidgets, isScoped, hasSmartContent, customWidgetsData, customWidgetsByType) => {
    if (!isScoped(SMART_CONTENT)) {
      return defaultState;
    }

    const {
      isEmail
    } = meta;
    const globalWidget = widget.type === 'global_widget' && globalWidgets.widgetsByName[widget.name];
    const widgetType = globalWidget ? globalWidget.widgetType : widget.type;
    let isEligibleForSmartContent;

    if (widget.type === 'global_widget') {
      isEligibleForSmartContent = false;
    } else if (widget.type === 'custom_widget') {
      const formModuleId = customWidgetsByType.form && customWidgetsByType.form.moduleId;
      const customWidget = findCustomWidget(widget, customWidgetsData);

      if (customWidget) {
        const moduleId = customWidget.moduleId;
        const isCMv2Module = customWidget.schemaVersion > 1;
        const isBlogContentModule = customWidget.path === '@hubspot/blog_content';
        const isFormModuleWithoutForm = moduleId === formModuleId && !widget.params.form;
        isEligibleForSmartContent = isCMv2Module && !isBlogContentModule && !isFormModuleWithoutForm;
      } else {
        isEligibleForSmartContent = false;
      }
    } else if (widgetType === SmartWidgetTypes.form) {
      isEligibleForSmartContent = !!widget.form;
    } else {
      isEligibleForSmartContent = !!SmartWidgetTypes[widgetType];
    }

    const anonymousTargeting = !(isEmail && widget.type !== 'global_widget');
    state = state.merge({
      isEligibleForSmartContent,
      hasSmartContent,
      anonymousTargeting,
      targetType: getTargetType(widgetType)
    });
    return state;
  });
};
export const makeGetWidgetSmartObjects = () => {
  const getSmartWidget = makeGetSmartWidget();
  const empty = SeamlessImmutable({});
  let smartObjects = empty;
  return createSelector([getSmartWidget], widget => {
    const {
      smart_objects: widgetSmartObjects
    } = widget.getSmartProperties();

    if (!(widgetSmartObjects && widgetSmartObjects.length)) {
      return empty;
    }

    const newSmartObjects = {};
    widgetSmartObjects.forEach(smartObject => {
      const {
        criterion_id: id
      } = smartObject;
      newSmartObjects[id] = smartObject;
    });
    Object.keys(smartObjects).forEach(smartObjectId => {
      if (newSmartObjects[smartObjectId]) {
        return;
      }

      smartObjects = smartObjects.without(smartObjectId);
    });
    smartObjects = smartObjects.merge(newSmartObjects);
    return smartObjects;
  });
};
export const makeGetIsWidgetSplittable = () => {
  return createSelector([getWidget, getIsInFlexColumn], (widget, isInFlexColumn) => {
    if (isInFlexColumn) {
      return false;
    }

    if (widget.w === 1) {
      return false;
    }

    return true;
  });
};
export const makeGetCanCellUngroup = () => {
  return createSelector([getWidget, getLayoutData], (widget, layoutData) => {
    // Must be a `cell` with one row of widgets.
    if (widget.type !== 'cell') {
      return false;
    }

    if (widget.rows.length !== 1) {
      return false;
    } // Number of widgets in row cannot exceed width of cell being ungrouped.


    const rowId = widget.rows[0];
    const row = layoutData.rows[rowId];

    if (row.length > widget.w) {
      return false;
    }

    return true;
  });
};
export const makeGetRowWidgetSummaries = () => {
  const empty = SeamlessImmutable({});
  let rowWidgets = empty;
  return createSelector([getRow, getLayoutData], (row, layoutData) => {
    if (!(row && row.length)) {
      return empty;
    }

    const newRowWidgets = {};
    row.forEach(widgetId => {
      const {
        id,
        type
      } = layoutData.widgets[widgetId];
      newRowWidgets[widgetId] = {
        id,
        type
      };
    }); // Remove widgets no longer in row.

    Object.keys(rowWidgets).forEach(widgetId => {
      if (newRowWidgets[widgetId]) {
        return;
      }

      rowWidgets = rowWidgets.without(widgetId);
    });
    rowWidgets = rowWidgets.merge(newRowWidgets, {
      deep: true
    });
    return rowWidgets;
  });
};
/**
 * Required Props:
 *  none - state only selector
 *
 * Returns a function that will get data for the widget from the widget catalogs.
 *
 * label - Correct label to display for widget.
 * typeLabel - The widgets type label. (E.g., "Global Group", "Call-to-action", etc.)
 * isOrphan - True if global/custom widget or global group has been removed (deleted) from catalog.
 * icon -
 *
 * @return {Function} layoutWidget => {label, typeLabel, isOrphan, icon}
 */

export const getWidgetCatalogDataForWidget = function () {
  return createSelector([getCatalogWidgetGetterForLayoutWidget, getWidgetCatalogIsInitialized, getCustomWidgetsByType], (getCatalogWidget, widgetCatalogIsInitialized, customWidgetsByType) => {
    return layoutWidget => {
      const catalogWidget = getCatalogWidget(layoutWidget);
      const hasEditableContentFields = catalogWidget && catalogWidget.fields ? !!catalogWidget.fields.length : false;
      const catalogWidgetLabel = getWidgetLabel(catalogWidget);
      const label = catalogWidgetLabel || layoutWidget.label || '';
      const helpText = layoutWidget.help || '';
      const isOrphan = widgetCatalogIsInitialized && !catalogWidget;
      const blogContentModuleId = customWidgetsByType.blog_content && customWidgetsByType.blog_content.moduleId;
      const emailBodyModuleId = customWidgetsByType.email_body && customWidgetsByType.email_body.moduleId;

      switch (layoutWidget.type) {
        case 'custom_widget':
          {
            const catalogWidgetHelpText = catalogWidget && catalogWidget.helpText;
            return {
              isOrphan,
              label: layoutWidget.label,
              helpText: helpText || catalogWidgetHelpText || '',
              typeLabel: catalogWidgetLabel || getI18nWidgetTypeLabel(layoutWidget.type),
              icon: catalogWidget ? catalogWidget.icon : undefined,
              hasEditableContentFields,
              isBlogContent: catalogWidget && catalogWidget.moduleId === blogContentModuleId,
              isEmailBody: catalogWidget && catalogWidget.moduleId === emailBodyModuleId,
              hasHtmlField: catalogWidget && containsHtmlField(catalogWidget.fields)
            };
          }

        case 'global_group':
          {
            return {
              isOrphan,
              label,
              typeLabel: getI18nWidgetTypeLabel(layoutWidget.type),
              icon: undefined,
              hasEditableContentFields
            };
          }

        case 'global_widget':
          {
            return {
              isOrphan,
              label,
              typeLabel: getI18nWidgetTypeLabel(layoutWidget.type),
              icon: undefined,
              hasEditableContentFields
            };
          }

        case 'section_delimiter':
          {
            return {
              isOrphan: false,
              label: text('designdata.widgetCatalog.widgets.section_delimiter.title'),
              typeLabel: getI18nWidgetTypeLabel(layoutWidget.type),
              icon: undefined,
              hasEditableContentFields
            };
          }

        default:
          {
            return {
              isOrphan,
              // Use the passed widget value. If it is a layout widget the user can change the value.
              label: layoutWidget.label || '',
              helpText,
              typeLabel: getI18nWidgetTypeLabel(layoutWidget.type),
              icon: undefined,
              hasEditableContentFields,
              isBlogContent: layoutWidget.type === 'blog_content',
              isEmailBody: layoutWidget.type === 'email_body',
              hasHtmlField: layoutWidget.type === 'raw_html'
            };
          }
      }
    };
  });
}();
/**
 * Required Props:
 *  layoutId || assetId
 * Optional Props:
 *  revisionId
 */

export const makeGetLayoutSectionData = () => {
  const defaultData = SeamlessImmutable({
    isHeaderEmpty: false,
    isBodyEmpty: false,
    isFooterEmpty: false,
    firstDelimiterId: null,
    firstDelimiterIndex: null
  });
  let state = defaultData;
  return createSelector([getLayoutData], layoutData => {
    if (!(layoutData && layoutData.rootWidgetId)) {
      state = defaultData;
      return state;
    }

    const {
      rootWidgetId,
      widgets,
      rows
    } = layoutData;
    const rootWidget = widgets[rootWidgetId];
    const {
      rows: rootRows
    } = rootWidget; // If zero rows, then we have an empty global group.

    const {
      length
    } = rootRows;

    if (length === 0) {
      state = state.merge(Object.assign({}, defaultData, {
        isBodyEmpty: true
      }));
      return state;
    }

    const data = Object.assign({}, defaultData); // If HEADER is empty, then the FIRST widget will be a delimiter.

    const firstRow = rows[rootRows[0]];
    const firstWidget = widgets[firstRow[0]];
    data.isHeaderEmpty = isSectionDelimiterType(firstWidget.type); // If FOOTER is empty, then the LAST widget will be a delimiter.

    const lastRow = rows[rootRows[length - 1]];
    const lastWidget = widgets[lastRow[0]];
    data.isFooterEmpty = isSectionDelimiterType(lastWidget.type); // If HEADER or FOOTER is empty, try to get other data without iteration.

    if (data.isHeaderEmpty) {
      const nextRow = rows[rootRows[1]];
      const nextWidget = widgets[nextRow[0]];
      const isNextWidgetDelimiter = isSectionDelimiterType(nextWidget.type);
      data.isBodyEmpty = isNextWidgetDelimiter;
      data.firstDelimiterId = firstWidget.id;
      data.firstDelimiterIndex = 0;
    } else if (data.isFooterEmpty) {
      const rowIndex = length - 2;
      const previousRow = rows[rootRows[rowIndex]];
      const previousWidget = widgets[previousRow[0]];
      const isPreviousWidgetDelimiter = isSectionDelimiterType(previousWidget.type);

      if (isPreviousWidgetDelimiter) {
        data.isBodyEmpty = isPreviousWidgetDelimiter;
        data.firstDelimiterId = previousWidget.id;
        data.firstDelimiterIndex = rowIndex;
      }
    } // If we've still not found the first delimiter,
    // find it via iteration and determine if body after it is empty.


    if (!data.firstDelimiterId) {
      for (let idx = 0; idx < length; idx++) {
        const row = rows[rootRows[idx]];
        const widget = widgets[row[0]];

        if (!isSectionDelimiterType(widget.type)) {
          continue;
        }

        data.firstDelimiterId = widget.id;
        data.firstDelimiterIndex = idx;
        const nextRow = rows[rootRows[idx + 1]];
        const nextWidget = widgets[nextRow[0]];
        const isNextDelimiter = isSectionDelimiterType(nextWidget.type);
        data.isBodyEmpty = isNextDelimiter;
        break;
      }
    }

    state = state.merge(data);
    return state;
  });
};
const EMPTY_LAYOUT_SECTIONS_HASH = SeamlessImmutable({
  widgets: {},
  rows: {}
});
/**
 * Creates a hash of LayoutSections by widget and row ids for a layout.
 * {
 *   widgets: { [widgetId]: LayoutSection },
 *   rows: { [rowId]: LayoutSection },
 * }
 *
 * @param {Object} meta
 * @param {Object} layoutData
 */

function createLayoutSectionsHash(meta, layoutData) {
  if (!(meta && layoutData)) {
    return EMPTY_LAYOUT_SECTIONS_HASH;
  }

  const hash = {
    widgets: {},
    rows: {}
  };
  const {
    widgets,
    rows,
    rootWidgetId
  } = layoutData;

  if (!meta.hasSectionDelimiters) {
    // eslint-disable-next-line guard-for-in
    for (const widgetId in widgets) {
      hash.widgets[widgetId] = widgetId === rootWidgetId ? VOID : SECTIONLESS;
    } // eslint-disable-next-line guard-for-in


    for (const rowId in rows) {
      hash.rows[rowId] = SECTIONLESS;
    }

    return hash;
  }

  const rootWidget = widgets[rootWidgetId];
  let delimiterCount = 0;
  someNormalizedWidgets(widgets, rows, rootWidgetId, (wgt, {
    rowId
  }) => {
    if (wgt === rootWidget) {
      hash.widgets[rootWidgetId] = VOID;
      return false;
    }

    const {
      id: widgetId
    } = wgt;

    if (isSectionDelimiterType(wgt.type)) {
      ++delimiterCount;
      const section = delimiterCount === 1 ? SECTION_DELIMITER_1 : SECTION_DELIMITER_2;
      hash.widgets[widgetId] = section;
      hash.rows[rowId] = section;
      return false;
    }

    let section;

    switch (delimiterCount) {
      case 0:
        section = HEADER;
        break;

      case 1:
        section = BODY;
        break;

      case 2:
        section = FOOTER;
        break;

      default:
        section = VOID;
        break;
    }

    hash.widgets[widgetId] = section;
    hash.rows[rowId] = section;
    return false;
  });
  return hash;
}
/**
 * Required props:
 *  - layoutId
 * Optional Props:
 *  revisionId
 */


export const getLayoutSectionHash = (() => {
  const getMeta = makeGetMeta();
  return createSelector([getMeta, getLayoutData], (meta, layoutData) => {
    if (meta.revisionId) {
      return EMPTY_LAYOUT_SECTIONS_HASH;
    }

    const hash = createLayoutSectionsHash(meta, layoutData);
    return SeamlessImmutable(hash);
  });
})();
/**
 * Required Props:
 *  layoutId
 * Optional Props:
 *  revisionId
 */

export const getWidgetAncestry = createSelector([getLayoutData], generateWidgetAncestry);
/**
 * Required Props:
 *  layoutId
 *  widgetId
 * Optional Props:
 *  revisionId
 */

export const makeGetWidgetContextualData = () => {
  const getMeta = makeGetMeta();
  const defaultData = SeamlessImmutable({
    widgetId: null,
    parentId: null,
    rowId: null,
    isRoot: false,
    isRootChild: false,
    isInFlexColumn: false,
    isInVirtualCell: false,
    isInVirtualBody: false,
    layoutSection: VOID
  });
  let contextData = defaultData;
  return createSelector([getWidgetIdFromProps, getBodyEditable, getMeta, getWidgetAncestry, getLayoutSectionHash], (widgetId, bodyEditable, meta, widgetAncestry, layoutSectionHash) => {
    let node = widgetAncestry[widgetId];

    if (!node) {
      contextData = defaultData;
      return contextData;
    }

    const layoutSection = layoutSectionHash.widgets[widgetId];
    const updateData = {
      widgetId,
      layoutSection,
      rowId: node.data.rowId,
      parentId: node.data.parentId,
      isRoot: node.data.isRoot,
      // Determine if immediate child of root
      isRootChild: !!(node.next && node.next.data.isRoot),
      isInVirtualBody: bodyEditable && meta.isVirtualBodyCapable && layoutSection === BODY,
      isInFlexColumn: !!(node.data.parent && isFlexColumnType(node.data.parent.type)),
      isInVirtualCell: false
    }; // Iterate up ancestry to determine container context.

    for (; node; node = node.next) {
      const {
        parent
      } = node.data;
      if (!parent) break;

      if (isVirtualCellType(parent.type)) {
        updateData.isInVirtualCell = true;
        break;
      }
    }

    return contextData = SeamlessImmutable.merge(contextData, updateData);
  });
};
export const makeGetCanWidgetBeConvertedToFlexColumn = () => {
  const getStructuralWidgets = makeGetStructuralWidgets();
  const getMeta = makeGetMeta();
  return createSelector([getWidget, getLayoutData, getStructuralWidgets, getLayoutSectionHash, getMeta, getBodyEditable], (widget, layoutData, structuralWidgets, widgetToLayoutSectionHash, meta, bodyEditable) => {
    if (widget.type !== 'cell') {
      return false;
    }

    const {
      widgets: {
        widget_container
      }
    } = structuralWidgets;

    if (!widget_container) {
      return false;
    } // If virtual body is activated and module is in body, it cannot be converted.


    if (meta.isVirtualBodyCapable && bodyEditable) {
      const section = widgetToLayoutSectionHash.widgets[widget.id];

      if (section === BODY) {
        return false;
      }
    }

    if (isWidgetEmpty(widget)) {
      return true;
    }

    const {
      widgets,
      rows
    } = layoutData; // Check if structure of the cell's rows/widgets are valid for a flex column.

    const isInvalid = someNormalizedWidgets(widgets, rows, widget.id, wgt => {
      if (wgt === widget) {
        return false;
      }

      if (wgt.w !== 12) {
        return true;
      }

      if (wgt.type === 'global_group') {
        return true;
      }

      if (isCellType(wgt.type)) {
        return true;
      }

      return false;
    });
    return !isInvalid;
  });
};

function isTypeInvalidForVirtualChildren(type) {
  if (type === 'global_group') {
    return true;
  }

  if (isVirtualCellType(type)) {
    return true;
  }

  if (isFlexColumnType(type)) {
    return true;
  }

  return false;
}

function areParamsInvalidForVirtualChildren(params) {
  if (params.definition_id != null) {
    return true;
  }

  return false;
}

function isTypeInvalidForBodyEditable(type) {
  if (type === 'cell') {
    return false;
  }

  if (type === 'custom_widget') {
    return false;
  }

  return true;
}
/**
 * Required props:
 *  - layoutId
 *  - widgetId
 * Optional props:
 *  - revisionId
 */


export const makeGetCanWidgetBeConvertedToVirtualCell = () => {
  const getStructuralWidgets = makeGetStructuralWidgets();
  const getMeta = makeGetMeta();
  return createSelector([getWidget, getLayoutData, getStructuralWidgets, getShowLayoutSectionEditorOptions, getLayoutSectionHash, getMeta, getBodyEditable], (widget, layoutData, structuralWidgets, showLayoutSectionEditorOptions, widgetToLayoutSectionHash, meta, bodyEditable) => {
    if (!showLayoutSectionEditorOptions) {
      return false;
    }

    if (widget.type !== 'cell') {
      return false;
    }

    if (meta.isVirtualBodyCapable && bodyEditable) {
      return false;
    }

    const {
      widgets: {
        virtual_cell
      }
    } = structuralWidgets;

    if (!virtual_cell) {
      return false;
    }

    const section = widgetToLayoutSectionHash.widgets[widget.id];

    if (section !== BODY) {
      return false;
    }

    if (isWidgetEmpty(widget)) {
      return true;
    }

    const {
      widgets,
      rows
    } = layoutData; // Check if the group contains invalid children

    const isInvalid = someNormalizedWidgets(widgets, rows, widget.id, wgt => {
      if (wgt === widget) {
        return false;
      }

      if (isTypeInvalidForVirtualChildren(wgt.type)) {
        return true;
      }

      return false;
    });
    return !isInvalid;
  });
};
/**
 * Required Props:
 *  - layoutId
 * Optional Props:
 *  - revisionId
 */

export const getInvalidVirtualBodyWidgets = (() => {
  const getMeta = makeGetMeta();
  const cache = new WeakMap();
  const empty = SeamlessImmutable([]);
  return createSelector([getMeta, getLayoutData, getLayoutSectionHash], (meta, layoutData, layoutSectionHash) => {
    if (meta.revisionId) {
      return empty;
    }

    if (!layoutData) {
      return empty;
    }

    const invalid = [];
    const {
      widgets
    } = layoutData;
    const widgetsSectionHash = layoutSectionHash.widgets; // eslint-disable-next-line guard-for-in

    for (const widgetId in widgets) {
      const wgt = widgets[widgetId];

      switch (widgetsSectionHash[widgetId]) {
        case BODY:
          if (isTypeInvalidForBodyEditable(wgt.type)) {
            invalid.push(wgt);
          } else if (areParamsInvalidForVirtualChildren(wgt.params)) {
            invalid.push(wgt);
          }

          break;

        default:
          if (isVirtualCellType(wgt.type)) {
            invalid.push(wgt);
          }

          break;
      }
    }

    if (!invalid.length) {
      return empty;
    }

    const cached = cache.get(meta);

    if (cached && cached.length === invalid.length && cached.every(x => invalid.includes(x))) {
      return cached;
    }

    const result = SeamlessImmutable(invalid);
    cache.set(meta, result);
    return result;
  });
})();

const getLanguageOverride = (state, props) => props.languageOverride;

export const makeGetWidgetParams = () => {
  const empty = SeamlessImmutable({});
  return createSelector([getWidget, getLanguageOverride], (widget, languageOverride) => {
    if (!widget) return empty;
    if (!languageOverride) return widget.params;
    const languageOverrides = widget.getIn(['languageOverrides', languageOverride]) || empty;
    return widget.params.merge(languageOverrides);
  });
};
/*
 * canBeMadeGlobal
 */

export const makeCanWidgetBeMadeGlobal = function () {
  const canWidgetBeMadeGlobal = ({
    catalogWidget,
    hasSmartContent,
    isBlogContent,
    widget
  }) => {
    return catalogWidget && !catalogWidget.global && catalogWidget.schemaVersion > 1 && !hasSmartContent && !isBlogContent && widget.type !== 'raw_jinja';
  };

  const canCellBeMadeGlobal = ({
    widget,
    customWidgetsData,
    meta,
    layoutData,
    bodyEditable,
    layoutSectionHash
  }) => {
    if ( // Only cells can be made global.
    widget.type !== 'cell' || // Cannot nest global groups.
    meta.isGlobalGroup || // Emails layouts cannot have global groups.
    meta.isEmail || // Cell must have children.
    isWidgetEmpty(widget)) {
      return false;
    } // Virtual bodies cannot contain global groups


    if (bodyEditable && meta.isVirtualBodyCapable) {
      const section = layoutSectionHash.widgets[widget.id];

      if (section === BODY) {
        return false;
      }
    }

    const {
      widgets,
      rows
    } = layoutData; // Check if structure of the cells rows/widgets are valid for a flex column.

    const isInvalid = someNormalizedWidgets(widgets, rows, widget.id, wgt => {
      if (wgt === widget) {
        return false;
      }

      if (isFlexColumnType(wgt.type) || isVirtualCellType(wgt.type)) {
        return true;
      } // TODO - Look into allowing global groups within global groups


      if (wgt.type === 'global_group') {
        return true;
      }

      if (wgt.type === 'custom_widget') {
        const catalogWidget = findCustomWidget(wgt, customWidgetsData); // if we don't know about this widget, skip

        if (!catalogWidget) {
          return true;
        } // only CMv2 widgets are allowed to be globally grouped, and only in the IDE


        if (catalogWidget.schemaVersion == 1) {
          return true;
        }
      }

      return false;
    });
    return !isInvalid;
  };

  return () => {
    const getCatalogWidget = makeGetCatalogWidgetForLayoutWidget();
    const getMeta = makeGetMeta();
    return createSelector([getWidget, getCustomWidgetsData, getCatalogWidget, getMeta, getLayoutData, getHasSmartContent, getWidgetCatalogDataForWidget, getLayoutSectionHash, getBodyEditable], (widget, customWidgetsData, catalogWidget, meta, layoutData, hasSmartContent, widgetCatalogData, layoutSectionHash, bodyEditable) => {
      const {
        isBlogContent
      } = widgetCatalogData(widget);
      return isCellType(widget.type) ? canCellBeMadeGlobal({
        widget,
        customWidgetsData,
        meta,
        layoutData,
        bodyEditable,
        layoutSectionHash
      }) : canWidgetBeMadeGlobal({
        widget,
        catalogWidget,
        hasSmartContent,
        isBlogContent
      });
    });
  };
}();